import React, { useCallback, useEffect } from 'react';
import {
  MdClose,
  MdStopCircle,
  MdHistoryToggleOff,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Avatar,
  AVATAR_SIZE_SMALL,
  EmptyState,
  IconButton,
  Sheet,
  Spinner,
} from '@aibuild/ui';
import {
  cancelRun,
  type ProcessRunListItemDto,
  type RunStatus,
} from '@api/processRuns';
import useSnackbar from '@hooks/useSnackbar';
import { processRunsListQueryKey } from '../../hooks/processRunsQueryKeys';
import { formatRelativeTime, formatDuration } from './RunHistorySheet.utils';
import {
  DurationInline,
  IdCell,
  LoadingWrap,
  MetaLine,
  MetaMono,
  MetaNote,
  MetaOwner,
  MetaSep,
  NameBlock,
  NameCell,
  NameText,
  NameTitle,
  RowActions,
  RowCard,
  RowTable,
  RowWrap,
  RunId,
  RunRows,
  SheetPositionWrap,
  StatusDot,
  StatusPill,
  StopButton,
  StopSpacer,
  TrailingCell,
} from './RunHistorySheet.styled';

export interface RunHistorySheetProps {
  processId: string;
  processName: string;
  runs: readonly ProcessRunListItemDto[];
  isLoading: boolean;
  onClose: () => void;
  dataTestId?: string;
}

const STATUS_COLOR: Record<RunStatus, string> = {
  completed: '#4ade80',
  failed: '#f87171',
  cancelled: 'var(--on-surface-variant, #94a3b8)',
  running: 'color-mix(in oklch, var(--primary, #C1C1FF) 90%, white)',
  pending: 'var(--on-surface-variant, #94a3b8)',
  waiting_for_action: 'var(--on-surface-variant, #94a3b8)',
};

const STATUS_LABEL: Record<RunStatus, string> = {
  completed: 'Succeeded',
  failed: 'Failed',
  cancelled: 'Cancelled',
  running: 'In progress',
  pending: 'Pending',
  waiting_for_action: 'Waiting',
};

const RunHistorySheet: React.FC<RunHistorySheetProps> = ({
  processId,
  processName,
  runs,
  isLoading,
  onClose,
  dataTestId = 'run-history-sheet',
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleRowClick = useCallback(
    (runId: string) => {
      navigate(`/processes/${processId}/runs/${runId}`);
    },
    [navigate, processId],
  );

  const queryClient = useQueryClient();
  const { showError } = useSnackbar();

  const cancelMutation = useMutation({
    mutationFn: (runId: string) => cancelRun(processId, runId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: processRunsListQueryKey(processId),
      });
    },
    onError: () => {
      showError('Failed to cancel run. Please try again.');
    },
  });

  const handleStop = useCallback(
    (runId: string) => {
      cancelMutation.mutate(runId);
    },
    [cancelMutation],
  );

  const isStoppable = (status: RunStatus) =>
    status === 'running' || status === 'pending' || status === 'waiting_for_action';

  const buildNotes = (run: ProcessRunListItemDto): string | null => {
    if (run.failureReason) return run.failureReason;
    if (run.status === 'cancelled') return 'Cancelled by user';
    return null;
  };

  let bodyContent: React.ReactNode;
  if (isLoading) {
    bodyContent = (
      <LoadingWrap data-testid={`${dataTestId}__loading`}>
        <Spinner size="md" />
      </LoadingWrap>
    );
  } else if (runs.length === 0) {
    bodyContent = (
      <EmptyState
        icon={<MdHistoryToggleOff aria-hidden="true" />}
        title="No runs in history."
        dataTestId={`${dataTestId}__empty`}
      />
    );
  } else {
    bodyContent = (
      <RunRows data-testid={`${dataTestId}__list`}>
            {runs.map((run) => {
              const notes = buildNotes(run);
              const color = STATUS_COLOR[run.status];
              return (
                <RowWrap key={run.runId}>
                  <RowCard
                    data-testid={`${dataTestId}__row-${run.runId}`}
                    onClick={() => handleRowClick(run.runId)}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick(run.runId);
                      }
                    }}
                    role="link"
                    tabIndex={0}
                  >
                    <RowTable>
                      <tbody>
                        <tr>
                          <IdCell>
                            <RunId data-testid={`${dataTestId}__id-${run.runId}`}>
                              #{run.runNumber}
                            </RunId>
                          </IdCell>
                          <NameCell>
                            <NameBlock>
                              {isStoppable(run.status) ? (
                                <StopButton
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStop(run.runId);
                                  }}
                                  title="Stop this run"
                                  aria-label="Stop this run"
                                  data-testid={`${dataTestId}__stop-${run.runId}`}
                                >
                                  <MdStopCircle />
                                </StopButton>
                              ) : (
                                <StopSpacer aria-hidden="true" />
                              )}
                              <NameText>
                                <NameTitle>
                                  Run #{run.runNumber}
                                  <DurationInline>
                                    {formatDuration(run.durationMs)}
                                  </DurationInline>
                                </NameTitle>
                                <MetaLine>
                                  <MetaOwner title={`Started by ${run.startedBy.name}`}>
                                    <Avatar
                                      name={run.startedBy.name}
                                      size={AVATAR_SIZE_SMALL}
                                    />
                                    {run.startedBy.name}
                                  </MetaOwner>
                                  <MetaSep>&middot;</MetaSep>
                                  <MetaMono>
                                    {formatRelativeTime(run.createdAt)}
                                  </MetaMono>
                                  {notes && (
                                    <>
                                      <MetaSep>&middot;</MetaSep>
                                      <MetaNote>{notes}</MetaNote>
                                    </>
                                  )}
                                </MetaLine>
                              </NameText>
                            </NameBlock>
                          </NameCell>
                          <TrailingCell>
                            <RowActions>
                              <StatusPill $color={color}>
                                {run.status === 'running' ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <StatusDot $color={color} />
                                )}
                                {STATUS_LABEL[run.status]}
                              </StatusPill>
                            </RowActions>
                          </TrailingCell>
                        </tr>
                      </tbody>
                    </RowTable>
                  </RowCard>
                </RowWrap>
              );
            })}
          </RunRows>
    );
  }

  return (
    <SheetPositionWrap>
      <Sheet
        ariaLabel={`Run history for ${processName}`}
        dataTestId={dataTestId}
      >
        <Sheet.Header dataTestId={`${dataTestId}__header`}>
          <Sheet.HeaderLead>
            <Sheet.Title dataTestId={`${dataTestId}__title`}>
              Runs History
            </Sheet.Title>
          </Sheet.HeaderLead>
          <IconButton
            ariaLabel="Close run history"
            title="Close run history"
            icon={<MdClose />}
            onClick={onClose}
            dataTestId={`${dataTestId}__close`}
          />
        </Sheet.Header>
        <Sheet.Body dataTestId={`${dataTestId}__body`}>
          {bodyContent}
        </Sheet.Body>
      </Sheet>
    </SheetPositionWrap>
  );
};

export default RunHistorySheet;
